import { useState } from "react";
import { Controller } from "react-hook-form";
import moment from "moment";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TextField from "@mui/material/TextField";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { TModalProps } from "./DatePicker.type";
import { dateToString } from "../../../utils/dateToString";
import { format, isValid } from 'date-fns';

/**
 * paginationItems
 * @param {label} label Label of Input
 * @param {Boolean} readOnly Boolean value to set field as readOnly
 * @param {Function} onChange Function to handle onChange of Input Field.
 * @param {Number} minDate Min date in years EX: 100 would be 100 years in the past
 * @param {Number} maxDate Max date in years EX: 18 would be 18 years in the past
 * @param {Function} error Error Handling
 * @param {Boolean} required Set Field as required
 * @param {Function} shouldDisableDate Function to Select which dates are disabled
 * @param {Function} onChange Function to handle Select Change
 */

const INPUT_COLOR_ERROR = "red";

function validarFormatoFecha(fecha: string): boolean {
  const patron = /^(Sun|Mon|Tue|Wed|Thu|Fri|Sat)\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s\d{2}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT[-+]\d{4}\s\((\w|\s)+\)$/;
  return patron.test(fecha);
}

function convertirFormatoFecha(fecha: string): string {
  try {
    const fechaObj = new Date(fecha);
    // Verificar si la fecha es válida antes de formatear usando isValid de date-fns
    if (!isValid(fechaObj)) {
      return fecha; // Retornar el valor original si es inválido
    }
    return format(fechaObj, "EEE MMM dd yyyy HH:mm:ss 'GMT'XXX '(Argentina Standard Time)'");
  } catch (error) {
    // Si hay algún error al formatear, retornar el valor original
    return fecha;
  }
}

export default function BasicDatePicker({
  fieldName,
  label,
  minDate,
  maxDate,
  error,
  control,
  required,
  shouldDisableDate,
  onChange,
  readOnly,
  defaultAvailableDate = null,
  disabled,
  styles,
}: TModalProps) {
  const INPUT_COLOR = error === undefined ? "#D0D0D0" : "red";
  const theme = createTheme({
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              color: "rgba(0, 0, 0)",
            },
            "&.MuiInputLabel-formControl": {
              color: "rgba(68, 68, 68, 1)",
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {},
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: INPUT_COLOR,
          },
        },
      },
    },
  });

  let minimum = moment().subtract(minDate, "years").toDate();
  let maximum = moment().add(maxDate, "years").toDate();

  const [firstOpened, setFirstOpened] = useState<any>(false);
  const [chosenDate, setChosenDate] = useState<any>(
    "Sun May 26 2022 00:00:00 GMT-0300 (Argentina Standard Time)",
  );

  const [dateFormatted, setFormattedDate] = useState("");

  return (
    <>
      <ThemeProvider theme={theme}>
        <LocalizationProvider adapterLocale={es} dateAdapter={AdapterDateFns}>
          <Controller
            name={fieldName}
            control={control}
            defaultValue={defaultAvailableDate}
            render={({ field }) => {
              return (
                <DesktopDatePicker
                  className={styles}
                  disabled={disabled}
                  readOnly={readOnly}
                  label={label}
                  maxDate={maximum}
                  minDate={minimum}
                  inputFormat="dd/MM/yyyy"
                  shouldDisableDate={shouldDisableDate}
                  onChange={(value) => {
                    if (value) {
                      setFirstOpened(true);
                      setChosenDate(value);
                      setFormattedDate(dateToString(value));
                    }

                    onChange && onChange(value);
                    return field.onChange(value);
                  }}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{ ...params.inputProps, placeholder: "dd/mm/aaaa" }}
                    />)}
                />
              );
            }}
            rules={{
              required,
              validate: (value) => {
                if (!disabled) {
                  // Si el valor es null, undefined o vacío
                  if (!value) {
                    return !required || "Este campo es requerido";
                  }
                  
                  let fechaObj: Date;
                  
                  // Si el valor es un objeto Date, usar directamente
                  if (value instanceof Date) {
                    fechaObj = value;
                  } 
                  // Si es un string, intentar parsearlo
                  else if (typeof value === 'string') {
                    // Si tiene el formato esperado (formato de fecha completa), parsear directamente
                    if (validarFormatoFecha(value)) {
                      fechaObj = new Date(value);
                    } else {
                      // Intentar convertir el formato
                      const fechaConvertida = convertirFormatoFecha(value);
                      // Si la conversión retornó el valor original, significa que es inválida
                      if (fechaConvertida === value) {
                        // Intentar parsear como fecha directamente (puede ser formato dd/MM/yyyy)
                        fechaObj = new Date(value);
                      } else {
                        fechaObj = new Date(fechaConvertida);
                      }
                    }
                  } else {
                    // Tipo no reconocido
                    return "La fecha introducida no es válida*";
                  }
                  
                  // Verificar si la fecha es válida usando isValid de date-fns
                  if (!isValid(fechaObj)) {
                    // Si la fecha es inválida, permitir que el usuario continúe escribiendo
                    // Solo validar cuando el campo pierde el foco o se envía el formulario
                    return true; // Permitir valores parciales mientras se escribe
                  }
                  
                  // Validar rango de fechas
                  if (fechaObj < minimum || fechaObj > maximum) {
                    return "La fecha introducida no está autorizada*";
                  }
                  
                  return true;
                } else {
                  return true
                }
              }
            }}
          />
        </LocalizationProvider>
      </ThemeProvider>
      {/* ERRORS MESSAGE */}
      {error?.message && (
        <span style={{ color: INPUT_COLOR_ERROR }}>{error?.message}</span>
      )}
      {error?.type === "validate" && (
        <span style={{ color: INPUT_COLOR_ERROR }}>La fecha introducida no está autorizada*</span>
      )}
      {error?.type === "required" && (
        <span style={{ color: INPUT_COLOR_ERROR }}>{label} es requerido*</span>
      )}
    </>
  );
}
